import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import type { Group, GroupStatus, OrganizerOrder, OrganizerDashboardStats, OrderPaymentStatus } from '../types/database';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CollectivePaymentModal from '../components/CollectivePaymentModal';
import Loader from '../components/Loader';
import { organizerDashboardService } from '../services/organizerDashboard.service';
import OrganizerGroupAddress from '../components/OrganizerGroupAddress';
import { pickGroupDeliveryAddress } from '../components/DeliveryAddressForm';

const STATUS_LABELS: Record<OrderPaymentStatus, string> = {
  cancelled: 'Cancelado',
  free: 'Gratuito',
  paid_tpv: 'Pagado (TPV)',
  paid_collected: 'Pagado',
  pending: 'Pendiente',
};

// Interfaz para las revisiones de diseño
interface DesignRevision {
  id: string;
  design_id: string;
  type: 'solicitud' | 'respuesta';
  pdf_url: string | null;
  image_url: string | null;
  text: string | null;
  created_at: string;
  updated_at: string;
}

export default function OrganizerDashboard() {
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [orders, setOrders] = useState<OrganizerOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrganizerOrder[]>([]);
  const [dashboardStats, setDashboardStats] = useState<OrganizerDashboardStats | null>(null);
  const [statuses, setStatuses] = useState<GroupStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingConfirmation, setIsUpdatingConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCollectivePaymentModal, setShowCollectivePaymentModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [organizerId, setOrganizerId] = useState<string | null>(null);
  const [latestDesignRevision, setLatestDesignRevision] = useState<DesignRevision | null>(null);
  const [expandedCustomizations, setExpandedCustomizations] = useState<Set<string>>(new Set());
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteOrderData, setDeleteOrderData] = useState<{orderId: string, customerName: string} | null>(null);
  const [showGroupConfirmModal, setShowGroupConfirmModal] = useState(false);
  const [pendingConfirmationData, setPendingConfirmationData] = useState<{type: 'organizer', value: boolean} | null>(null);
  const [ordersWithHistory, setOrdersWithHistory] = useState<Map<string, {
    current: OrganizerOrder;
    totalPaid: number;
    isChange: boolean;
    isFree: boolean;
  }>>(new Map());

  console.log("group", group);

  // Carga inicial para obtener el ID del grupo desde localStorage
  useEffect(() => {
    const storedUserType = localStorage.getItem('userType');
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserType || !storedUserData || storedUserType !== 'organizer') {
      navigate('/');
      return;
    }

    try {
      const parsedUserData = JSON.parse(storedUserData);
      const currentGroupId = parsedUserData.group?.id;
      const currentOrganizerId = parsedUserData.user?.id;
      
      if (!currentGroupId) {
        toast.error('Error al cargar datos del grupo');
        return;
      }
      
      if (!currentOrganizerId) {
        toast.error('Error al cargar información del usuario');
        return;
      }
      
      // Guardar el ID del grupo y organizador para usarlo en consultas posteriores
      setGroupId(currentGroupId);
      setOrganizerId(currentOrganizerId);
    } catch (error) {
      toast.error('Error al cargar información del usuario');
      navigate('/');
    }
  }, [navigate]);

  const refreshOrdersAndStats = useCallback(async () => {
    if (!groupId) return;
    try {
      const dashboard = await organizerDashboardService.getDashboard(groupId);
      setOrders(dashboard.orders);
      setDashboardStats(dashboard.stats);

      const orderMap = new Map<string, {
        current: OrganizerOrder;
        totalPaid: number;
        isChange: boolean;
        isFree: boolean;
      }>();
      for (const order of dashboard.orders) {
        orderMap.set(order.id, {
          current: order,
          totalPaid: parseFloat(Number(order.total_price).toFixed(2)),
          isChange: false,
          isFree: order.payment_status === 'free',
        });
      }
      setOrdersWithHistory(orderMap);
    } catch {
      // Silent refresh — don't toast on background re-fetch
    }
  }, [groupId]);

  // Función para cargar todos los datos del dashboard
  const loadDashboardData = useCallback(async () => {
    if (!groupId) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Obtener datos del grupo directamente de la base de datos usando el ID
        const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select(`
          *,
          delivery_addresses!fk_delivery_addresses_group_id (*),
          design:designs!designs_group_id_fkey(
            *,
            latest_revision:design_revisions(
              *
            )
          ),
          status:group_statuses (*),
          group_history (*)
        `)
        .eq('id', groupId)
        .single();

      if (groupError) {
        toast.error(`Error al cargar datos del grupo: ${groupError.message}`);
        return;
      }
      
      if (!groupData) {
        toast.error('No se encontró el grupo solicitado');
        return;
      }
      
      // Actualizar el estado con los datos más recientes del grupo
      setGroup(groupData);
      
      // Actualizar también en localStorage para mantener sincronizado
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        parsedUserData.group = groupData;
        localStorage.setItem('userData', JSON.stringify(parsedUserData));
      }
      
      // Obtener estados de grupo
      const { data: statusesData, error: statusesError } = await supabase
        .from('group_statuses')
        .select('*')
        .order('id');

      if (statusesError) {
        toast.error('Error al cargar estados del grupo');
      } else {
        setStatuses(statusesData || []);
      }

      // Cargar pedidos y stats desde el backend endpoint
      try {
        const dashboard = await organizerDashboardService.getDashboard(groupId);
        setOrders(dashboard.orders);
        setDashboardStats(dashboard.stats);

        const orderMap = new Map<string, {
          current: OrganizerOrder;
          totalPaid: number;
          isChange: boolean;
          isFree: boolean;
        }>();

        for (const order of dashboard.orders) {
          orderMap.set(order.id, {
            current: order,
            totalPaid: parseFloat(Number(order.total_price).toFixed(2)),
            isChange: false,
            isFree: order.payment_status === 'free',
          });
        }
        setOrdersWithHistory(orderMap);
      } catch {
        toast.error('Error al cargar los pedidos');
        setOrders([]);
        setOrdersWithHistory(new Map());
      }

      // Procesar la última revisión del diseño
      if (groupData.design && groupData.design.latest_revision && groupData.design.latest_revision.length > 0) {
        const sortedRevisions = [...groupData.design.latest_revision].sort((a: DesignRevision, b: DesignRevision) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setLatestDesignRevision(sortedRevisions[0]);
      } else {
        setLatestDesignRevision(null);
      }
    } catch (error) {
      toast.error('Error inesperado al cargar los datos');
    } finally {
      setIsLoading(false);
    }
  }, [groupId]);

  // Efecto para cargar datos cuando tenemos un ID de grupo válido
  useEffect(() => {
    if (groupId) {
      loadDashboardData();
    }
  }, [loadDashboardData, groupId]);

  useEffect(() => {
    // Filtrar órdenes cuando cambia el término de búsqueda o las órdenes
    if (searchTerm === '') {
      setFilteredOrders(orders);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = orders.filter(order => {
        return (
          order.name?.toLowerCase().includes(lowercasedFilter) ||
          order.product?.garment_name?.toLowerCase().includes(lowercasedFilter) ||
          order.product?.color_name?.toLowerCase().includes(lowercasedFilter) ||
          order.product?.size_name?.toLowerCase().includes(lowercasedFilter)
        );
      });
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  const handleConfirmationUpdate = async (type: 'organizer', value: boolean) => {
    if (!group || !groupId) return;
    
    // Si está intentando confirmar (value = true) y no es fase de diseño, mostrar modal de confirmación
    const isDesignPhase = group.status_id > 3;
    if (value && !isDesignPhase) {
      setPendingConfirmationData({ type, value });
      setShowGroupConfirmModal(true);
      return;
    }
    
    // Proceder directamente si es desconfirmar o es fase de diseño
    await executeConfirmationUpdate(type, value);
  };

  const executeConfirmationUpdate = async (type: 'organizer', value: boolean) => {
    if (!group || !groupId) return;
    
    setIsUpdatingConfirmation(true);
    const toastId = toast.loading('Actualizando confirmación...');
    
    try {
      // Determinar si debemos actualizar la confirmación del grupo o del diseño
      // Si el estado es mayor que 3 (fase de diseño o posterior), actualizamos la confirmación del diseño
      const isDesignPhase = group.status_id > 3;

      if (isDesignPhase && group.design) {
        // Actualizar la confirmación del diseño usando RPC
        const { error: designUpdateError } = await supabase.rpc('update_design_organizer_confirmation', {
          p_design_id: group.design.id,
          p_confirmation_value: value
        });
          
        if (designUpdateError) {
          toast.error('Error al actualizar confirmación', { id: toastId });
          return;
        }

        const { error } = await supabase.rpc('add_group_history', {
          p_group_id: groupId,
          p_action_type: "organizer_design_confirmed",
          p_metadata: {
            confirmation_value: value
          },
          p_notes: "Confirmación actualizada por el organizador",
        })

        if (error) {
          console.error(error);
          throw new Error("Failed to create design history");
        }
      } else {
        // Actualizar la confirmación del grupo usando la función RPC
        const { error } = await supabase.rpc('update_group_confirmation', {
          p_group_id: groupId,
          p_confirmation_type: type,
          p_value: value
        });
        
        if (error) {
          toast.error('Error al actualizar confirmación', { id: toastId });
          return;
        }
      }
      
      // Recargar los datos para tener todo actualizado
      loadDashboardData();
      
      toast.success('Confirmación actualizada', { id: toastId });
    } catch (error) {
      toast.error('Error al actualizar confirmación', { id: toastId });
    } finally {
      setIsUpdatingConfirmation(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userType');
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    toast.success('Has cerrado sesión correctamente');
    navigate('/');
  };

  const handleRefreshData = async () => {
    if (!groupId) {
      toast.error('No se pudo actualizar: información de grupo no disponible');
      return;
    }
    
    const toastId = toast.loading('Actualizando datos...');
    
    try {
      // Cargar datos frescos directamente
      await loadDashboardData();
      toast.success('Datos actualizados', { id: toastId });
    } catch (error) {
      toast.error('Error al actualizar los datos', { id: toastId });
    }
  };

  const handleToggleCollectivePayment = async () => {
    // Abrir el nuevo modal de pagos colectivos
    setShowCollectivePaymentModal(true);
  };

  const handleCloseCollectivePaymentModal = () => {
    setShowCollectivePaymentModal(false);
  };

  const handlePaymentSubmitted = () => {
    refreshOrdersAndStats();
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const calculateTotalAmount = useCallback(() => {
    return dashboardStats?.total_orderable_amount ?? 0;
  }, [dashboardStats]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    // Validar tipos de archivo permitidos
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Tipo de archivo no permitido: ${file.type}. Solo se permiten: JPEG, PNG, WebP, PDF`);
        e.target.value = ''; // Limpiar input
        return;
      }
    }
    
    setSelectedFile(file);
    
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const handleUploadReceipt = async () => {
    if (!selectedFile || !group || !groupId) return;
    
    setIsUploadingReceipt(true);
    const toastId = toast.loading('Subiendo comprobante...');
    
    try {
      // Crear nombre único para el archivo
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${groupId}_receipt_${Date.now()}.${fileExt}`;
      
      // Subir archivo a Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('receipts')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        toast.error('Error al subir el archivo', { id: toastId });
        return;
      }

      // Obtener URL pública del archivo
      const { data: urlData } = supabase.storage
        .from('receipts')
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;

      // Usar RPC para actualizar la URL del comprobante en el grupo
      const userData = localStorage.getItem('userData');
      const parsedUserData = JSON.parse(userData || '{}');
      const organizerId = parsedUserData.user?.id;

      const { error: rpcError } = await supabase.rpc('update_group_receipt', {
        p_group_id: groupId,
        p_organizer_id: organizerId,
        p_receipt_url: publicUrl
      });

      if (rpcError) {
        toast.error('Error al guardar el comprobante', { id: toastId });
        return;
      }

      // Actualizar estado local
      const updatedGroup = { ...group, payment_receipt_url: publicUrl };
      setGroup(updatedGroup);
      
      // Actualizar localStorage
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        parsedUserData.group = updatedGroup;
        localStorage.setItem('userData', JSON.stringify(parsedUserData));
      }

      // Limpiar preview
      setSelectedFile(null);
      setPreviewUrl(null);
      
      toast.success('Comprobante subido correctamente', { id: toastId });
    } catch (error) {
      toast.error('Error al subir el comprobante', { id: toastId });
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleRemoveReceipt = async () => {
    if (!group || !groupId || !group.payment_receipt_url) return;
    
    const toastId = toast.loading('Eliminando comprobante...');
    
    try {
      // Extraer nombre del archivo de la URL
      const url = group.payment_receipt_url;
      const fileName = url.split('/').pop()?.split('?')[0]; // Obtener solo el nombre del archivo
      
      if (fileName) {
        // Eliminar archivo del storage
        const { error: deleteError } = await supabase.storage
          .from('receipts')
          .remove([fileName]);

        if (deleteError) {
          // Continúa aunque falle la eliminación del archivo
        }
      }

      // Usar RPC para actualizar el grupo
      const userData = localStorage.getItem('userData');
      const parsedUserData = JSON.parse(userData || '{}');
      const organizerId = parsedUserData.user?.id;

      const { error: rpcError } = await supabase.rpc('update_group_receipt', {
        p_group_id: groupId,
        p_organizer_id: organizerId,
        p_receipt_url: null
      });

      if (rpcError) {
        toast.error('Error al eliminar el comprobante', { id: toastId });
        return;
      }
      
      // Limpiar preview si existe
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Limpiar estado local
      setSelectedFile(null);
      setPreviewUrl(null);
      
      const updatedGroup = { ...group, payment_receipt_url: undefined };
      setGroup(updatedGroup);
      
      // Actualizar localStorage
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        parsedUserData.group = updatedGroup;
        localStorage.setItem('userData', JSON.stringify(parsedUserData));
      }
      
      toast.success('Comprobante eliminado', { id: toastId });
    } catch (error) {
      toast.error('Error al eliminar el comprobante', { id: toastId });
    }
  };





  // Mock de data para pruebas - corregir el error del linter
  useEffect(() => {
    // Solo si tenemos un grupo cargado pero sin el campo is_collective_payment
    if (group && !('is_collective_payment' in group) && typeof group === 'object') {
      // Añadir la propiedad is_collective_payment (por defecto true ya que no usamos el interruptor)
      const updatedGroup: Group = {
        ...group as Group,
        is_collective_payment: true,
        payment_receipt_url: undefined
      };
      setGroup(updatedGroup);
      
      // Actualizar en localStorage
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        parsedUserData.group = updatedGroup;
        localStorage.setItem('userData', JSON.stringify(parsedUserData));
      }
    }
  }, [group]);

  // Función para descargar el último diseño
  const handleDownloadDesign = useCallback(async () => {
    if (!latestDesignRevision) {
      toast.error('No hay diseño disponible para descargar');
      return;
    }
    
    const fileUrl = latestDesignRevision.pdf_url || latestDesignRevision.image_url;
    
    if (!fileUrl) {
      toast.error('No hay archivo de diseño disponible');
      return;
    }
    
    // Crear un elemento a invisible para descargar el archivo
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.download = `Diseño_${group?.name || 'grupo'}.${fileUrl.split('.').pop()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Descarga iniciada');
  }, [latestDesignRevision, group]);

  // Función para exportar pedidos a Excel (sin datos sensibles)
  const handleExportOrdersToExcel = useCallback(() => {
    const activeOrders = orders.filter(order => order.payment_status !== 'cancelled');
    
    if (activeOrders.length === 0) {
      toast.error('No hay pedidos para exportar');
      return;
    }
    
    const headers = group?.is_collective_payment 
      ? ['Nombre', 'Producto', 'Color', 'Talla', 'Personalizaciones', 'Precio', 'Estado de Pago']
      : ['Nombre', 'Producto', 'Color', 'Talla', 'Personalizaciones'];
    
    const rows = activeOrders.map(order => {
      const customizations = order.customizations
        ?.filter(c => c.value && c.value !== 'false')
        ?.map(c => {
          if (c.value === 'true') {
            return c.option_name || '';
          }
          return `${c.option_name}: ${c.value}`;
        })
        ?.join(', ') || '';
      
      const rowData: (string | number)[] = [
        order.name || '',
        order.product?.garment_name || '',
        order.product?.color_name || '',
        order.product?.size_name || '',
        customizations
      ];

      if (group?.is_collective_payment) {
        rowData.push(`${Number(order.total_price || 0).toFixed(2)} €`);
        rowData.push(STATUS_LABELS[order.payment_status]);
      }

      return rowData;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');

    const fileName = `Pedidos_${group?.name || 'grupo'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    toast.success('Pedidos exportados correctamente');
  }, [orders, group]);

  // Función para alternar la expansión de personalizaciones
  const toggleCustomizationExpansion = (orderId: string) => {
    setExpandedCustomizations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Función para renderizar personalizaciones de forma compacta
  const renderCustomizations = (order: OrganizerOrder, isExpanded: boolean) => {
    if (!order.customizations || order.customizations.length === 0) {
      return <span className="text-gray-400 italic text-xs">Sin personalización</span>;
    }

    const validCustomizations = order.customizations.filter(custom => 
      custom.value && 
      custom.value !== 'false' && 
      custom.value !== '' && 
      custom.value !== '0'
    );

    if (validCustomizations.length === 0) {
      return <span className="text-gray-400 italic text-xs">Sin personalización</span>;
    }

    if (!isExpanded) {
      // Vista compacta: mostrar solo la cantidad y un botón para expandir
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600">
            {validCustomizations.length} pers.
          </span>
          <button
            onClick={() => toggleCustomizationExpansion(order.id)}
            className="text-blue-600 hover:text-blue-800 text-xs underline"
          >
            Ver
          </button>
        </div>
      );
    }

    // Vista expandida: mostrar todo el detalle
    return (
      <div>
        <div className="flex items-center gap-1 mb-1">
          <button
            onClick={() => toggleCustomizationExpansion(order.id)}
            className="text-blue-600 hover:text-blue-800 text-xs underline"
          >
            Ocultar
          </button>
        </div>
        <ul className="divide-y divide-gray-100 rounded bg-gray-50 p-1 text-xs">
          {validCustomizations.map((custom, idx) => (
            <li key={idx} className="py-0.5 truncate">
              <span className="text-gray-500">{custom.option_name}:</span>
              <span className="font-medium text-gray-900 ml-1">{custom.value}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleTogglePaymentStatus = async (orderId: string) => {
    if (!group?.is_collective_payment) {
      toast.error('Esta función solo está disponible para grupos de pago colectivo');
      return;
    }

    const targetOrder = orders.find(o => o.id === orderId);
    if (!targetOrder || (targetOrder.payment_status !== 'pending' && targetOrder.payment_status !== 'paid_collected')) {
      toast.error('Este pedido no se puede modificar');
      return;
    }

    const isCurrentlyCollected = targetOrder.payment_status === 'paid_collected';

    try {
      const userData = localStorage.getItem('userData');
      const parsedUserData = JSON.parse(userData || '{}');
      const organizerId = parsedUserData.user?.id;
      
      if (!organizerId) {
        toast.error('Error: No se encontró ID del organizador');
        return;
      }

      const { data, error } = await supabase.rpc('update_order_payment_status', {
        p_order_id: orderId,
        p_organizer_id: organizerId,
        p_collected_by_organizer: !isCurrentlyCollected
      });

      if (error) {
        toast.error(`Error al actualizar estado: ${error.message}`);
        return;
      }

      if (data && !data.success) {
        toast.error(`Error: ${data.error}`);
        return;
      }

      const newStatus: OrderPaymentStatus = isCurrentlyCollected ? 'pending' : 'paid_collected';

      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, payment_status: newStatus }
            : order
        )
      );

      toast.success(`Reserva marcada como ${!isCurrentlyCollected ? 'pagada' : 'no pagada'}`);

      refreshOrdersAndStats();
    } catch (error) {
      toast.error(`Error inesperado: ${error instanceof Error ? error.message : 'Desconocido'}`);
    }
  };

  // Función para eliminar una reserva (solo para grupos colectivos)
  const handleDeleteReservation = async (orderId: string, customerName: string) => {
    if (!group?.is_collective_payment) {
      toast.error('Esta función solo está disponible para grupos de pago colectivo');
      return;
    }

    // Abrir modal de confirmación
    setDeleteOrderData({ orderId, customerName });
    setShowDeleteConfirmModal(true);
  };

  // Función para confirmar la eliminación de la reserva
  const confirmDeleteReservation = async () => {
    if (!deleteOrderData) return;

    const { orderId, customerName } = deleteOrderData;

    try {
      // Usar función RPC para eliminar orden con personalizaciones
      const { error } = await supabase.rpc('delete_group_order', {
        p_order_id: orderId
      });

      if (error) {
        toast.error('Error al eliminar la reserva');
        return;
      }

      // Actualizar el estado local
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      setOrdersWithHistory(prevMap => {
        const newMap = new Map(prevMap);
        newMap.delete(orderId);
        return newMap;
      });

      // Actualizar unidades vendidas del grupo en estado local
      if (group) {
        const updatedGroup = { ...group, units_sold: Math.max(0, group.units_sold - 1) };
        setGroup(updatedGroup);
      }

      toast.success(`Reserva de ${customerName} eliminada correctamente`);

      // Cerrar modal
      setShowDeleteConfirmModal(false);
      setDeleteOrderData(null);
    } catch (error) {
      toast.error('Error inesperado al eliminar la reserva');
    }
  };

  // Función para cancelar la eliminación
  const cancelDeleteReservation = () => {
    setShowDeleteConfirmModal(false);
    setDeleteOrderData(null);
  };

  // Función para confirmar la confirmación del grupo
  const confirmGroupConfirmation = async () => {
    if (!pendingConfirmationData) return;
    
    await executeConfirmationUpdate(pendingConfirmationData.type, pendingConfirmationData.value);
    setShowGroupConfirmModal(false);
    setPendingConfirmationData(null);
  };

  // Función para cancelar la confirmación del grupo
  const cancelGroupConfirmation = () => {
    setShowGroupConfirmModal(false);
    setPendingConfirmationData(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl font-medium text-red-600">Error al cargar los datos del grupo</div>
      </div>
    );
  }

  const hasOrganizerPriceDelta = filteredOrders.some(
    (order) => Number(order.organizer_price_delta) !== 0
  );

  const isDesignPhase = group.status_id > 3;
  const isOrganizerConfirmed = isDesignPhase
    ? Boolean(group.design?.organizer_confirmation)
    : group.organizer_confirmation;
  const designConfirmedAt = group.group_history?.find(
    (history) => history.action_type === 'organizer_design_confirmed'
  )?.created_at;
  const commercialDesignConfirmedAt = group.group_history?.find(
    (history) => history.action_type === 'commercial_design_confirmed'
  )?.created_at;
  const currentStatus = statuses.find((s) => s.id === group.status_id);

  return (
    <div className="min-h-screen flex flex-col" style={{backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'}}>
      <Header />
      
      <main className="flex-grow pt-10">
        {/* Banner superior con información del grupo */}
        <div className="bg-[#F90000] text-white">
          <div className="max-w-7xl mx-auto py-3 sm:py-4 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-full sm:w-auto">
                <h1 className="text-lg sm:text-2xl font-bold truncate">
                  {group.name}
                </h1>
                <p className="text-sm sm:text-base">Panel de Organizador</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="bg-white text-[#F90000] py-2 px-3 sm:px-4 rounded-lg font-mono font-bold text-sm sm:text-lg shadow-md text-center">
                  Código: {group.code}
                </div>
                <button 
                  onClick={handleLogout}
                  className="bg-white/20 hover:bg-white/30 text-white font-medium py-2 px-3 sm:px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm text-sm sm:text-base"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                  </svg>
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 space-y-4">
          {/* Estadísticas del grupo - más compactas en una sola fila */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${
              group.is_collective_payment ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
            }`}
          >
            {/* Unidades */}
            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-[#F90000] overflow-hidden min-w-0 h-full">
              <div className="flex items-center justify-between mb-2 gap-2 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-500 flex-shrink-0">Unidades</h3>
                <p className="text-base sm:text-xl font-bold flex-shrink-0 whitespace-nowrap">
                  {dashboardStats?.total_orders ?? 0} / {group.units}
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 sm:h-2.5">
                <div
                  className="bg-[#F90000] h-2 sm:h-2.5 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, ((dashboardStats?.total_orders ?? 0) / group.units) * 100)}%`,
                  }}
                />
              </div>
            </div>

            {/* Estado */}
            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-[#F90000] h-full">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-500 whitespace-nowrap">Estado</h3>
                <p
                  className="text-sm sm:text-lg font-bold text-right min-w-0 flex-shrink"
                  style={{ color: currentStatus?.color }}
                >
                  {currentStatus?.name}
                </p>
              </div>
            </div>

            {/* Confirmación organizador */}
            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-[#F90000] h-full">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-700">
                    {isDesignPhase ? 'Confirmación Diseño' : 'Confirmación Grupo'}
                  </h3>
                  <input
                    type="checkbox"
                    checked={isOrganizerConfirmed}
                    onChange={(e) => handleConfirmationUpdate('organizer', e.target.checked)}
                    className="rounded border-gray-300 text-[#F90000] focus:ring-red-500 h-5 w-5 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 cursor-pointer"
                    disabled={isUpdatingConfirmation || isOrganizerConfirmed}
                  />
                </div>
                <div
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${
                    isOrganizerConfirmed ? 'bg-green-50' : 'bg-amber-50'
                  }`}
                >
                  {isOrganizerConfirmed ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-green-700">Confirmado</span>
                      {designConfirmedAt && (
                        <span className="ml-auto text-green-700 text-xs sm:text-sm font-medium">
                          {new Date(designConfirmedAt).toLocaleDateString()}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-amber-700">
                        Pendiente de confirmación
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pagos — solo colectivos */}
            {group.is_collective_payment && (
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-[#F90000] h-full">
                <div className="flex flex-col gap-2">
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-500">Pagos por Plazos</h3>
                  <button
                    onClick={handleToggleCollectivePayment}
                    className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent shadow-sm text-xs sm:text-sm font-medium rounded-md text-white bg-[#F90000] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 w-full"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Gestionar Pagos
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Barra: envío + confirmación comercial */}
          <div className="bg-white rounded-lg shadow-sm px-3 sm:px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <OrganizerGroupAddress
              embedded
              groupId={group.id}
              groupName={group.name}
              deliveryOption={group.delivery_option}
              initialAddress={pickGroupDeliveryAddress(group.delivery_addresses)}
            />
            <div
              className={`flex items-center gap-1.5 flex-shrink-0 sm:ml-auto text-xs sm:text-sm font-medium ${
                commercialDesignConfirmedAt ? 'text-green-700' : 'text-amber-700'
              }`}
            >
              {commercialDesignConfirmedAt ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="whitespace-nowrap">
                    Comercial confirmado · {new Date(commercialDesignConfirmedAt).toLocaleDateString()}
                  </span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="whitespace-nowrap">Comercial pendiente</span>
                </>
              )}
            </div>
          </div>

          {/* Estadísticas de pago para grupos colectivos */}
          {group.is_collective_payment && (
            <div>
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-blue-500 overflow-hidden min-w-0">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 truncate">Estado de Pagos Colectivos</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                  <div className="text-center overflow-hidden min-w-0">
                    <div className="text-lg sm:text-xl font-bold text-green-600 truncate">
                      {dashboardStats?.organizer_collected_count ?? 0}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Recaudados</div>
                  </div>
                  <div className="text-center overflow-hidden min-w-0">
                    <div className="text-lg sm:text-xl font-bold text-red-600 truncate">
                      {dashboardStats?.organizer_pending_count ?? 0}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Pendientes</div>
                  </div>
                  <div className="text-center overflow-hidden min-w-0">
                    <div className="text-base sm:text-xl font-bold text-blue-600 truncate">
                      €{Number(dashboardStats?.organizer_collected_amount ?? 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Recaudado</div>
                  </div>
                  <div className="text-center overflow-hidden min-w-0">
                    <div className="text-base sm:text-xl font-bold text-gray-600 truncate">
                      €{(Number(dashboardStats?.organizer_collected_amount ?? 0) + Number(dashboardStats?.organizer_pending_amount ?? 0)).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Total a recaudar</div>
                  </div>
                </div>

                {(dashboardStats?.tpv_paid_count ?? 0) > 0 && (
                  <div className="mt-2 px-2 py-1.5 bg-blue-50 rounded text-xs text-blue-700">
                    {dashboardStats?.tpv_paid_count} pedido{(dashboardStats?.tpv_paid_count ?? 0) !== 1 ? 's' : ''} ya pagado{(dashboardStats?.tpv_paid_count ?? 0) !== 1 ? 's' : ''} por TPV (€{Number(dashboardStats?.tpv_paid_amount ?? 0).toFixed(2)})
                  </div>
                )}
                
                {/* Barra de progreso */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Progreso de recaudación</span>
                    <span>
                      {((dashboardStats?.organizer_collected_count ?? 0) + (dashboardStats?.organizer_pending_count ?? 0)) > 0 
                        ? Math.round(((dashboardStats?.organizer_collected_count ?? 0) / ((dashboardStats?.organizer_collected_count ?? 0) + (dashboardStats?.organizer_pending_count ?? 0))) * 100)
                        : 0
                      }%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${((dashboardStats?.organizer_collected_count ?? 0) + (dashboardStats?.organizer_pending_count ?? 0)) > 0 
                          ? ((dashboardStats?.organizer_collected_count ?? 0) / ((dashboardStats?.organizer_collected_count ?? 0) + (dashboardStats?.organizer_pending_count ?? 0))) * 100
                          : 0
                        }%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Lista de pedidos */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Título y contador */}
                <div className="flex items-center">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mr-2 sm:mr-3">Pedidos</h3>
                  <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                    {filteredOrders.filter(order => order.payment_status !== 'cancelled').length} pedidos
                  </span>
                </div>
                
                {/* Buscador y botones */}
                <div className="flex items-center gap-2">
                  {/* Buscador */}
                  <div className="relative flex-1 lg:flex-initial lg:min-w-[200px]">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar pedidos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 text-sm"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Iconos solo en móvil */}
                  <button 
                    onClick={handleRefreshData} 
                    disabled={isLoading}
                    className="lg:hidden shrink-0 p-2 border border-gray-300 rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 transition-colors"
                    title="Actualizar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button 
                    onClick={handleDownloadDesign}
                    disabled={!latestDesignRevision || (!latestDesignRevision.pdf_url && !latestDesignRevision.image_url)}
                    className="lg:hidden shrink-0 p-2 border border-gray-300 rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 transition-colors"
                    title="Descargar diseño"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button 
                    onClick={handleExportOrdersToExcel} 
                    disabled={orders.length === 0}
                    className="lg:hidden shrink-0 p-2 border border-gray-300 rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 transition-colors"
                    title="Exportar pedidos"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>

                  {/* Botones con texto en desktop */}
                  <div className="hidden lg:flex gap-2">
                    <button 
                      onClick={handleRefreshData} 
                      disabled={isLoading}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 whitespace-nowrap"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Actualizar
                    </button>
                    <button 
                      onClick={handleDownloadDesign}
                      disabled={!latestDesignRevision || (!latestDesignRevision.pdf_url && !latestDesignRevision.image_url)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 whitespace-nowrap"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Diseño
                    </button>
                    <button 
                      onClick={handleExportOrdersToExcel} 
                      disabled={orders.length === 0}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 whitespace-nowrap"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Exportar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Vista móvil: tarjetas */}
            <div className="lg:hidden space-y-3">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const orderWithHistory = ordersWithHistory.get(order.id);
                  return (
                    <div key={order.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${order.payment_status === 'cancelled' ? 'opacity-40' : ''}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className={`text-sm font-semibold text-gray-900 ${order.payment_status === 'cancelled' ? 'line-through' : ''}`}>
                          {order.name}
                        </div>
                        {order.payment_status === 'cancelled' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Cancelado</span>
                        )}
                        {group.is_collective_payment && order.payment_status !== 'cancelled' && (
                          <span className="text-base font-bold text-[#F90000]">
                            €{parseFloat(Number(order.total_price).toFixed(2)).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                        <span className="font-medium">{order.product?.garment_name || 'No disponible'}</span>
                        <span className="text-gray-300">•</span>
                        <div className="flex items-center gap-1">
                          {order.product?.color_hex && (
                            <div className="h-3 w-3 rounded-full border border-gray-300 shrink-0" style={{ backgroundColor: order.product.color_hex }} />
                          )}
                          <span>{order.product?.color_name || 'Sin color'}</span>
                        </div>
                        <span className="text-gray-300">•</span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 font-medium">
                          {order.product?.size_name || '—'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        {renderCustomizations(order, expandedCustomizations.has(order.id))}
                      </div>
                      {group.is_collective_payment && order.payment_status !== 'cancelled' && (
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            {orderWithHistory?.isChange && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">Cambio</span>
                            )}
                            {order.payment_status === 'free' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">Gratuito</span>
                            )}
                            {Number(order.organizer_price_delta) > 0 && (
                              <span className="text-xs text-gray-600">
                                Delta: +€{Number(order.organizer_price_delta).toFixed(2)}
                              </span>
                            )}
                            {order.organizer_price_delta !== undefined && Number(order.organizer_price_delta) < 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">Cambio</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {order.payment_status === 'paid_tpv' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                Pagado (TPV)
                              </span>
                            ) : order.payment_status === 'free' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                Gratuito
                              </span>
                            ) : (
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={order.payment_status === 'paid_collected'}
                                  onChange={() => handleTogglePaymentStatus(order.id)}
                                  className="sr-only peer"
                                />
                                <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500" />
                                <span className={`ml-1.5 text-xs font-medium ${order.payment_status === 'paid_collected' ? 'text-green-600' : 'text-gray-500'}`}>
                                  {order.payment_status === 'paid_collected' ? 'Pagado' : 'Pendiente'}
                                </span>
                              </label>
                            )}
                            <button
                              onClick={() => handleDeleteReservation(order.id, order.name ?? '')}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar reserva"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-gray-600 font-medium">
                    {orders.length > 0 ? 'No hay resultados para la búsqueda' : 'No hay pedidos en el grupo todavía'}
                  </p>
                  {orders.length > 0 && searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="mt-2 text-xs text-red-600 hover:text-red-800 font-medium">
                      Borrar búsqueda
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Vista desktop: tabla */}
            <div className="hidden lg:block shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed border-separate border-spacing-0">
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[16%]">
                        Contacto
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[24%]">
                        Producto
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[16%]">
                        Personalización
                      </th>
                      {group.is_collective_payment && (
                        <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[14%]">
                          Precio
                        </th>
                      )}
                      {group.is_collective_payment && hasOrganizerPriceDelta && (
                        <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[14%]">
                          Delta de organizador
                        </th>
                      )}
                      {group.is_collective_payment && (
                        <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[18%]">
                          Estado de Pago
                        </th>
                      )}
                      {group.is_collective_payment && (
                        <th scope="col" className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50 bg-opacity-75 px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-tight backdrop-blur backdrop-filter w-[12%]">
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((order, index) => {
                        const orderWithHistory = ordersWithHistory.get(order.id);
                        const isLast = index === filteredOrders.length - 1;
                        
                        return (
                          <tr key={order.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors ${order.payment_status === 'cancelled' ? 'opacity-30 bg-red-50' : ''}`}>
                            <td className={`px-2 py-2 ${isLast ? '' : 'border-b border-gray-200'}`}>
                              <div className={`text-sm font-medium text-gray-900 truncate ${order.payment_status === 'cancelled' ? 'line-through' : ''}`}>
                                {order.name}
                                {order.payment_status === 'cancelled' && (
                                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Cancelado
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`px-2 py-2 ${isLast ? '' : 'border-b border-gray-200'}`}>
                              <div className="flex items-center gap-1.5 text-sm">
                                <span className="font-medium text-gray-900 truncate">
                                  {order.product?.garment_name || 'No disponible'}
                                </span>
                                <span className="text-gray-300">•</span>
                                <div className="flex items-center gap-1">
                                  {order.product?.color_hex && (
                                    <div className="h-3 w-3 flex-shrink-0 rounded-full border border-gray-300" style={{ backgroundColor: order.product.color_hex }} />
                                  )}
                                  <span className="text-gray-600 text-xs truncate">
                                    {order.product?.color_name || 'Sin color'}
                                  </span>
                                </div>
                                <span className="text-gray-300">•</span>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {order.product?.size_name || '—'}
                                </span>
                              </div>
                            </td>
                            <td className={`px-2 py-2 ${isLast ? '' : 'border-b border-gray-200'}`}>
                              {renderCustomizations(order, expandedCustomizations.has(order.id))}
                            </td>
                            {group.is_collective_payment && (
                              <td className={`px-2 py-2 text-right ${isLast ? '' : 'border-b border-gray-200'}`}>
                                <div className="flex flex-col items-end">
                                  <div className="text-base font-bold text-[#F90000]">
                                    €{parseFloat(Number(order.total_price).toFixed(2)).toFixed(2)}
                                  </div>
                                  {orderWithHistory?.isChange && Number(order.organizer_price_delta) > 0 && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Total pagado: <span className="font-medium">€{parseFloat(orderWithHistory.totalPaid.toFixed(2)).toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="mt-2">
                                    {orderWithHistory?.isChange ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">Cambio</span>
                                    ) : orderWithHistory?.isFree ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">Gratuito</span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">Normal</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            )}
                            {group.is_collective_payment && hasOrganizerPriceDelta && (
                              <td className={`px-2 py-2 text-right ${isLast ? '' : 'border-b border-gray-200'}`}>
                                {Number(order.organizer_price_delta) > 0 ? (
                                  <div className="text-sm font-medium text-gray-700">
                                    +€{Number(order.organizer_price_delta).toFixed(2)}
                                  </div>
                                ) : Number(order.organizer_price_delta) < 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">Cambio</span>
                                ) : null}
                              </td>
                            )}
                            {group.is_collective_payment && (
                              <td className={`px-2 py-2 text-center ${isLast ? '' : 'border-b border-gray-200'}`}>
                                <div className="flex items-center justify-center">
                                  {order.payment_status === 'cancelled' ? (
                                    <span className="text-xs font-medium text-gray-400">N/A</span>
                                  ) : order.payment_status === 'paid_tpv' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                      Pagado (TPV)
                                    </span>
                                  ) : order.payment_status === 'free' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                      Gratuito
                                    </span>
                                  ) : (
                                    <label className="inline-flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={order.payment_status === 'paid_collected'}
                                        onChange={() => handleTogglePaymentStatus(order.id)}
                                        className="sr-only peer"
                                      />
                                      <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500" />
                                      <span className={`ml-1.5 text-xs font-medium ${order.payment_status === 'paid_collected' ? 'text-green-600' : 'text-gray-500'}`}>
                                        {order.payment_status === 'paid_collected' ? 'Pagado' : 'Pendiente'}
                                      </span>
                                    </label>
                                  )}
                                </div>
                              </td>
                            )}
                            {group.is_collective_payment && (
                              <td className={`px-2 py-2 text-center ${isLast ? '' : 'border-b border-gray-200'}`}>
                                {order.payment_status === 'cancelled' ? (
                                  <span className="text-xs font-medium text-gray-400">Sin acciones</span>
                                ) : (
                                  <button
                                    onClick={() => handleDeleteReservation(order.id, order.name ?? '')}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Eliminar reserva"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={group.is_collective_payment ? (hasOrganizerPriceDelta ? 7 : 6) : 3} className="px-2 py-10 text-center text-sm text-gray-500 border-b border-gray-200 bg-white">
                          <div className="flex flex-col items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-gray-600 font-medium">
                              {orders.length > 0 ? 'No hay resultados para la búsqueda' : 'No hay pedidos en el grupo todavía'}
                            </p>
                            {orders.length > 0 && searchTerm && (
                              <button 
                                onClick={() => setSearchTerm('')}
                                className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                Borrar búsqueda
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modal de pagos colectivos */}
      {showCollectivePaymentModal && groupId && organizerId && (
        <CollectivePaymentModal
          isOpen={showCollectivePaymentModal}
          onClose={handleCloseCollectivePaymentModal}
          groupId={groupId}
          organizerId={organizerId}
          onPaymentSubmitted={handlePaymentSubmitted}
        />
      )}

      {/* Modal de confirmación para eliminar reserva */}
      {showDeleteConfirmModal && deleteOrderData && (
        <div className="fixed inset-0 z-[99999] overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            {/* Overlay de fondo */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={cancelDeleteReservation}
            ></div>

            {/* Contenido del modal */}
            <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
              {/* Icono de advertencia */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Eliminar Reserva
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        ¿Estás seguro de que quieres eliminar la reserva de <span className="font-semibold text-gray-900">{deleteOrderData.customerName}</span>? Esta acción no se puede deshacer.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDeleteReservation}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Eliminar
                </button>
                <button
                  type="button"
                  onClick={cancelDeleteReservation}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para confirmación de grupo */}
      {showGroupConfirmModal && (
        <div className="fixed inset-0 z-[99999] overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            {/* Overlay de fondo */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={cancelGroupConfirmation}
            ></div>

            {/* Contenido del modal */}
            <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
              {/* Icono de información */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Confirmar Grupo
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        La confirmación de este Excel implica la aceptación de la información contenida en el pedido. Una vez confirmado, el pedido se mandará a producción y no podrá modificarse.
                      </p>
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                          <strong>Importante:</strong> Asegúrate de revisar todos los pedidos antes de confirmar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmGroupConfirmation}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#F90000] text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Confirmar Grupo
                </button>
                <button
                  type="button"
                  onClick={cancelGroupConfirmation}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de pago colectivo */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[99999] overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            {/* Overlay de fondo */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleClosePaymentModal}
            ></div>

            {/* Contenido del modal */}
            <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              {/* Cabecera del modal */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-bold text-gray-900" id="modal-title">
                    Información de Pago Colectivo
                  </h3>
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={handleClosePaymentModal}
                  >
                    <span className="sr-only">Cerrar</span>
                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
        </div>
              </div>
              
              {/* Cuerpo del modal */}
              <div className="bg-gray-50 px-4 py-5 sm:p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Información de transferencia */}
                  <div className="flex-1 md:border-r md:border-gray-200 md:pr-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-3">Información de Pago</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-500 block">Cantidad total:</span>
                          <span className="text-lg font-bold text-[#F90000]">€{calculateTotalAmount().toFixed(2)}</span>
                        </div>
                        
                        <div>
                          <span className="text-sm text-gray-500 block">Beneficiario:</span>
                          <span className="font-medium">Skowel Esstilo S.L.</span>
                        </div>
                        
                        <div>
                          <span className="text-sm text-gray-500 block">IBAN:</span>
                          <div 
                            className="font-mono bg-gray-50 p-2 rounded border border-gray-200 select-all cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText('ES19 0081 5338 3700 0158 7859');
                              toast.success('IBAN copiado al portapapeles');
                            }}
                            title="Haz clic para copiar"
                          >
                            ES19 0081 5338 3700 0158 7859
                          </div>
                        </div>
                        
                        <div>
                          <span className="text-sm text-gray-500 block">Concepto:</span>
                          <div 
                            className="font-mono bg-gray-50 p-2 rounded border border-gray-200 select-all cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(`GRUPO-${group.code}-${group.name.substr(0, 10)}`);
                              toast.success('Concepto copiado al portapapeles');
                            }}
                            title="Haz clic para copiar"
                          >
                            GRUPO-{group.code}-{group.name.substr(0, 10)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Comprobante de transferencia */}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-3">Comprobante de Transferencia</h4>
                    
                    {group.payment_receipt_url ? (
                      <div className="space-y-3">
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                          {group.payment_receipt_url.toLowerCase().includes('.pdf') ? (
                            <div 
                              className="flex flex-col items-center justify-center min-h-[200px] max-h-[400px] bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => window.open(group.payment_receipt_url, '_blank')}
                              title="Haz clic para ver el PDF"
                            >
                              <svg className="h-20 w-20 text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                              </svg>
                              <p className="text-sm font-medium text-gray-900">Comprobante PDF</p>
                              <p className="text-xs text-gray-500 mt-1">Haz clic para abrir</p>
                            </div>
                          ) : (
                            <img 
                              src={group.payment_receipt_url} 
                              alt="Comprobante de transferencia" 
                              className="w-full h-auto object-cover min-h-[200px] max-h-[400px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(group.payment_receipt_url, '_blank')}
                              title="Haz clic para ver en tamaño completo"
                            />
                          )}
                        </div>
                        
                        <button 
                          onClick={handleRemoveReceipt}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Eliminar comprobante
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg px-6 py-8 text-center bg-white">
                          {previewUrl ? (
                            <div className="mb-3">
                              {selectedFile?.type === 'application/pdf' ? (
                                <div className="flex flex-col items-center justify-center min-h-[200px] bg-gray-50 rounded-lg border border-gray-200">
                                  <svg className="h-16 w-16 text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                  </svg>
                                  <p className="text-sm font-medium text-gray-900">{selectedFile?.name}</p>
                                  <p className="text-xs text-gray-500 mt-1">Archivo PDF seleccionado</p>
                                </div>
                              ) : (
                                <img 
                                  src={previewUrl} 
                                  alt="Vista previa" 
                                  className="w-full h-auto object-cover min-h-[150px] max-h-[300px] rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(previewUrl, '_blank')}
                                  title="Haz clic para ver en tamaño completo"
                                />
                              )}
                            </div>
                          ) : (
                                                        <div className="space-y-4 py-8">
                              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <div className="text-center">
                                <p className="text-sm font-medium text-gray-900">
                                  Sube el comprobante de transferencia
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  JPEG, PNG, WebP o PDF hasta 20MB
                                </p>
                              </div>
                            </div>
      )}
                          
                          <div className="mt-3">
                            <label className="relative cursor-pointer bg-white rounded-md font-medium text-[#F90000] focus-within:outline-none">
                              <span className="inline-flex px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                Seleccionar archivo
                              </span>
                              <input 
                                type="file" 
                                className="sr-only" 
                                onChange={handleFileChange}
                                accept="image/jpeg,image/png,image/webp,application/pdf"
                              />
                            </label>
                          </div>
                        </div>
                        

                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Pie del modal */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedFile && !group.payment_receipt_url ? (
                  <>
                    <button
                      type="button"
                      onClick={handleUploadReceipt}
                      disabled={isUploadingReceipt}
                      className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#F90000] text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploadingReceipt ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Subir comprobante
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={handleClosePaymentModal}
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleClosePaymentModal}
                  >
                    Cerrar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
